import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { Button, Alert, Form } from 'react-bootstrap'
import { toast } from 'react-toastify'

import Card from '../_library/Card'
import BulkInvoiceDetails from './BulkInvoiceDetails'
import BulkInvoiceModals from './components/BulkInvoiceModals'
import ModalDeactivate from '../_library/ModalDeactivate'
import { 
  FETCH_BULK_INVOICE, 
  VOID_BULK_INVOICE, 
  SEND_BULK_INVOICE, 
  REFRESH_BULK_INVOICE,
  CREATE_DELEGATION 
} from '../../_common/redux/bulkInvoices/actions'

const BulkInvoiceViewPage = ({ event, currentInvoice, match, history, voidBulkInvoice, fetchBulkInvoice, refreshBulkInvoice, sendBulkInvoice, createDelegation }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showVoidModal, setShowVoidModal] = useState(false)
  const [showSendModal, setShowSendModal] = useState(false)
  const [showRefreshModal, setShowRefreshModal] = useState(false)
  const [voidReason, setVoidReason] = useState('')
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isCreatingDelegation, setIsCreatingDelegation] = useState(false)
  const [isVoiding, setIsVoiding] = useState(false)
  const [isSending, setIsSending] = useState(false)

  const invoiceId = match.params.invoiceId
  const eventId = match.params.id

  useEffect(() => {
    if (eventId && invoiceId) {
      setIsLoading(true)
      setError(null)
      
      fetchBulkInvoice(eventId, invoiceId)
        .then(() => {
          setIsLoading(false)
        })
        .catch((err) => {
          console.error('Failed to fetch invoice:', err)
          setError('Failed to load invoice. Please try again.')
          setIsLoading(false)
        })
    }
  }, [invoiceId, eventId])

  const fetchInvoice = async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      await fetchBulkInvoice(eventId, invoiceId)
    } catch (err) {
      console.error('Failed to fetch invoice:', err)
      setError('Failed to load invoice. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRefresh = () => {
    setShowRefreshModal(true)
  }

  const confirmRefresh = async () => {
    setIsRefreshing(true)
    try {
      await refreshBulkInvoice(eventId, invoiceId)
      setShowRefreshModal(false)
    } catch (err) {
      console.error('Failed to refresh invoice:', err)
      toast.error('Failed to refresh invoice. Please try again.')
      setShowRefreshModal(false)
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleBackToList = () => {
    history.push(`/event/${eventId}/bulk-invoices`)
  }

  const handleEdit = () => {
    history.push(`/event/${eventId}/bulk-invoices/${invoiceId}/edit`)
  }

  const handleSend = () => {
    setShowSendModal(true)
  }

  const confirmSend = async () => {
    setIsSending(true)
    try {
      await sendBulkInvoice(eventId, invoiceId)
      setShowSendModal(false)
    } catch (err) {
      console.error('Failed to send invoice:', err)
      toast.error('Failed to send invoice. Please try again.')
      setShowSendModal(false)
    } finally {
      setIsSending(false)
    }
  }

  const handleVoidInvoice = async () => {
    setIsVoiding(true)
    try {
      await voidBulkInvoice(eventId, invoiceId, voidReason)
      // After successful void, refresh the invoice data
      await fetchInvoice()
      setShowVoidModal(false)
      setVoidReason('')
    } catch (error) {
      // Error handling is done by the Redux action
      toast.error('Failed to void invoice. Please try again.')
      setShowVoidModal(false)
    } finally {
      setIsVoiding(false)
    }
  }

  const handleCreateDelegation = async () => {
    setIsCreatingDelegation(true)
    try {
      await createDelegation(eventId, invoiceId)
      // After successful delegation creation, refresh the invoice data
      await fetchInvoice()
    } catch (error) {
      // Error handling is done by the Redux action
      console.error('Failed to create delegation:', error)
    } finally {
      setIsCreatingDelegation(false)
    }
  }

  const handleViewDelegation = () => {
    history.push(`/event/${eventId}/delegations`)
  }

  // Simple boolean flags from backend - let backend handle all business logic
  // Handle both direct and nested (attributes) invoice data structures
  const invoiceData = currentInvoice?.attributes || currentInvoice
  const showDelegationButton = invoiceData?.can_create_delegation || false
  const showVoidButton = invoiceData?.can_void || false
  const showRefreshButton = invoiceData?.can_refresh || false
  const showEditButton = invoiceData?.can_edit || false
  const showSendButton = invoiceData?.can_send || false

  if (isLoading) {
    return (
      <div className="bulk-invoice-view-page">
        <Card title="Loading Invoice...">
          <div className="text-center p-4">
            <i className="fa fa-spinner fa-spin" /> Loading invoice details...
          </div>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bulk-invoice-view-page">
        <Card title="Error Loading Invoice">
          <Alert variant="danger">
            <h5>Error Loading Invoice</h5>
            <p>{error}</p>
          </Alert>
        </Card>
      </div>
    )
  }

  return (
    <div className="bulk-invoice-view-page">
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginRight: '15px' }}>
            <span>Invoice {currentInvoice ? currentInvoice.invoice_number : 'Loading...'}</span>
            <Button
              className="btn btn-outline-secondary"
              onClick={handleBackToList}
              style={{ marginLeft: 'auto' }}
            >
              <i className="fa fa-arrow-left" /> Back to Invoices
            </Button>
          </div>
        }
      >
        <BulkInvoiceDetails 
          invoice={currentInvoice}
          actionButtons={
            <div style={{display: 'flex', gap: '8px'}}>
              {showEditButton && (
                <Button 
                  className="btn btn-primary btn-outline"
                  size="sm" 
                  onClick={handleEdit}
                >
                  <i className="fa fa-edit" /> Edit
                </Button>
              )}
              {showSendButton && (
                <Button 
                  className="btn btn-secondary btn-shadow"
                  size="sm" 
                  onClick={handleSend}
                >
                  <i className="fa fa-paper-plane" /> Send
                </Button>
              )}
              {showVoidButton && (
                <Button 
                  className="btn btn-danger btn-shadow"
                  size="sm" 
                  onClick={() => setShowVoidModal(true)}
                >
                  <i className="fa fa-ban" /> Void
                </Button>
              )}
              {showRefreshButton && (
                <Button variant="secondary" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
                  {isRefreshing ? (
                    <><i className="fa fa-spinner fa-spin" /> Refreshing...</>
                  ) : (
                    <><i className="fa fa-sync" /> Refresh</>
                  )}
                </Button>
              )}
              {showDelegationButton && (
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleCreateDelegation}
                  disabled={isCreatingDelegation}
                >
                  {isCreatingDelegation ? (
                    <><i className="fa fa-spinner fa-spin" /> Creating Delegation...</>
                  ) : (
                    <><i className="fa fa-share-alt" /> Create Delegation</>
                  )}
                </Button>
              )}
              {invoiceData?.delegation_id && (
                <Button
                  variant="info"
                  size="sm"
                  onClick={handleViewDelegation}
                >
                  <i className="fa fa-eye" /> View Delegation
                </Button>
              )}
            </div>
          }
        />
        
        {/* Bulk Invoice Modals */}
        <BulkInvoiceModals
          showVoidModal={showVoidModal}
          voidInvoice={currentInvoice}
          voidReason={voidReason}
          setVoidReason={setVoidReason}
          onVoidCancel={() => {
            setShowVoidModal(false)
            setVoidReason('')
          }}
          onVoidConfirm={handleVoidInvoice}
          isVoiding={isVoiding}
          showSendModal={showSendModal}
          sendInvoice={currentInvoice}
          onSendCancel={() => setShowSendModal(false)}
          onSendConfirm={confirmSend}
          isSending={isSending}
        />

        <ModalDeactivate
          deleting={showRefreshModal}
          handleCancelDeactivate={() => setShowRefreshModal(false)}
          handleDeactivate={confirmRefresh}
          isLoading={isRefreshing}
          header="Refresh Invoice"
          content={`Are you sure you want to refresh invoice ${currentInvoice?.invoice_number} from Stripe? This will update the invoice with the latest data from Stripe.`}
          deactivateLabel="Refresh Invoice"
          deactivateClass="btn-secondary"
        />
      </Card>
    </div>
  )
}

const mapStateToProps = state => ({
  event: state.events.selected,
  currentInvoice: state.bulkInvoices.currentInvoice
})

const mapDispatchToProps = dispatch => ({
  voidBulkInvoice: (eventId, invoiceId, reason) => 
    dispatch(VOID_BULK_INVOICE(eventId, invoiceId, reason)),
  fetchBulkInvoice: (eventId, invoiceId) => dispatch(FETCH_BULK_INVOICE(eventId, invoiceId)),
  refreshBulkInvoice: (eventId, invoiceId) => dispatch(REFRESH_BULK_INVOICE(eventId, invoiceId)),
  sendBulkInvoice: (eventId, invoiceId) => dispatch(SEND_BULK_INVOICE(eventId, invoiceId)),
  createDelegation: (eventId, invoiceId) => dispatch(CREATE_DELEGATION(eventId, invoiceId))
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(BulkInvoiceViewPage))
